import { useEffect, useRef } from "react";
import utilsIndex from "../utils/index";

/**
 * 滾動追蹤 Hook
 * 當元素滾動超過螢幕中間時發送 GA 事件
 *
 * @param {Array} targets - 追蹤目標陣列，每個目標包含 { selector, blockName }
 * @param {string} eventName - GA 事件名稱
 * @param {boolean} enabled - 是否啟用追蹤（預設為 true）
 */
const useScrollTracking = (targets = [], eventName = "scroll_tracking", enabled = true) => {
  const trackedElements = useRef(new Set());
  const isInitialized = useRef(false);

  useEffect(() => {
    if (!enabled || targets.length === 0) {
      isInitialized.current = false;
      return;
    }

    // 如果已經初始化過，不再重複初始化
    if (isInitialized.current) return;
    isInitialized.current = true;

    const handleScroll = () => {
      const viewportMiddle = window.innerHeight / 2;

      targets.forEach(({ selector, blockName }) => {
        const element = document.querySelector(selector);

        if (!element) return;

        // 如果已經追蹤過這個元素，跳過
        if (trackedElements.current.has(selector)) return;

        const rect = element.getBoundingClientRect();
        const elementTop = rect.top;

        // 當元素頂部超過螢幕中間時觸發事件
        if (elementTop <= viewportMiddle && elementTop + rect.height > 0) {
          utilsIndex.sendGAEvent(eventName, {
            block_name: blockName,
          });

          // 標記為已追蹤
          trackedElements.current.add(selector);
        }
      });
    };

    // 初始檢查（處理頁面載入時已經在可視範圍內的元素）
    handleScroll();

    // 監聽滾動事件
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [targets, eventName, enabled]);

  // 提供重置追蹤狀態的方法
  const resetTracking = () => {
    trackedElements.current.clear();
  };

  return { resetTracking };
};

export default useScrollTracking;
